import { baseApi } from "@/shared/api";
import type {
  AttachedDocument,
  AttachedDocumentCreateRequest,
  AttachedDocumentUpdateRequest,
  AttachedDocumentsListParams,
  AttachedDocumentsListResponse,
} from "../types";

export const attachedDocumentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttachedDocuments: builder.query<AttachedDocumentsListResponse, AttachedDocumentsListParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set("page", String(params.page));
        if (params?.page_size) searchParams.set("page_size", String(params.page_size));
        if (params?.search) searchParams.set("search", params.search);
        const qs = searchParams.toString();
        return {
          url: `/api/legal/attached-documents/${qs ? `?${qs}` : ""}`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((doc) => ({ type: "AttachedDocuments" as const, id: doc.id })),
              { type: "AttachedDocuments" as const, id: "LIST" },
            ]
          : [{ type: "AttachedDocuments" as const, id: "LIST" }],
    }),

    getAttachedDocument: builder.query<AttachedDocument, number>({
      query: (id) => ({
        url: `/api/legal/attached-documents/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "AttachedDocuments" as const, id }],
      keepUnusedDataFor: 0,
    }),

    uploadAttachedDocument: builder.mutation<AttachedDocument, AttachedDocumentCreateRequest>({
      query: (body) => {
        const formData = new FormData();
        formData.append("file", body.file);
        formData.append("name", body.name);
        if (body.description) formData.append("description", body.description);
        return {
          url: "/api/legal/attached-documents/",
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
      invalidatesTags: [{ type: "AttachedDocuments" as const, id: "LIST" }],
    }),

    updateAttachedDocument: builder.mutation<AttachedDocument, { id: number; data: AttachedDocumentUpdateRequest }>({
      query: ({ id, data }) => {
        const formData = new FormData();
        if (data.name !== undefined) formData.append("name", data.name);
        if (data.description !== undefined) formData.append("description", data.description);
        if (data.file) formData.append("file", data.file);
        return {
          url: `/api/legal/attached-documents/${id}/`,
          method: "PATCH",
          body: formData,
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AttachedDocuments" as const, id: "LIST" },
        { type: "AttachedDocuments" as const, id },
        { type: "AuditEntries" as const, id: `attached-document-${id}` },
      ],
    }),

    deleteAttachedDocument: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/attached-documents/${id}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "AttachedDocuments" as const, id: "LIST" },
        { type: "AuditEntries" as const, id: `attached-document-${id}` },
      ],
    }),
  }),
});

export const {
  useGetAttachedDocumentsQuery,
  useGetAttachedDocumentQuery,
  useLazyGetAttachedDocumentQuery,
  useUploadAttachedDocumentMutation,
  useUpdateAttachedDocumentMutation,
  useDeleteAttachedDocumentMutation,
} = attachedDocumentsApi;
